import { Component } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { SupplierApiTesterService } from '../../../supplier-api-tester.service';
import { SupplierApiTesterClient } from '../../../supplier-api-tester.client';
import {
  DELAY_UNIT_LABELS,
  LINE_TYPE_LABELS,
  SupplierApiCall,
  SupplierApiListQuery,
  SupplierApiTicket,
  SupplierApiTicketLine,
} from '../../../supplier-api-tester.models';
import { ALL_LINES_QUERY, ApiTesterListBase } from '../api-tester-list-base';
import { ApiTesterActionService } from '../api-tester-action-dialog/api-tester-action.service';

/** Parcours guidé des expressions de besoin : liste, fiche avec ses lignes, décisions du fournisseur. */
@Component({
  selector: 'vex-api-tester-tickets',
  templateUrl: './api-tester-tickets.component.html',
  styleUrls: ['../api-tester-shared.scss'],
})
export class ApiTesterTicketsComponent extends ApiTesterListBase<SupplierApiTicket> {
  // `rejected` n'est pas un filtre accepté par l'API.
  readonly statusFilters = [
    { value: '', label: 'Tous' },
    { value: 'new', label: 'Nouveaux' },
    { value: 'confirmed', label: 'Confirmés' },
    { value: 'finalized', label: 'Finalisés' },
  ];
  readonly LINE_TYPE_LABELS = LINE_TYPE_LABELS;
  readonly DELAY_UNIT_LABELS = DELAY_UNIT_LABELS;

  ticket: SupplierApiTicket | null = null;
  lines: SupplierApiTicketLine[] = [];
  linesTotal = 0;
  detailLoading = false;
  detailError: string | null = null;

  constructor(
    supplierApiTesterService: SupplierApiTesterService,
    private readonly client: SupplierApiTesterClient,
    private readonly actionService: ApiTesterActionService,
  ) {
    super(supplierApiTesterService);
  }

  protected fetchList(query: SupplierApiListQuery): Observable<SupplierApiCall<SupplierApiTicket[]>> {
    return this.client.listTickets(query);
  }

  protected closeDetail(): void {
    this.ticket = null;
    this.lines = [];
    this.detailError = null;
  }

  openTicket(ticket: SupplierApiTicket): void {
    this.ticket = ticket;
    this.lines = [];
    this.loadDetail();
  }

  /** Relit la fiche et les lignes, comme le ferait le logiciel du fournisseur après une décision. */
  loadDetail(): void {
    const ticketId = this.ticket._id;
    this.detailLoading = true;
    forkJoin([this.client.getTicket(ticketId), this.client.getTicketLines(ticketId, ALL_LINES_QUERY)]).subscribe(
      ([ticketCall, linesCall]) => {
        this.detailLoading = false;
        this.ticket = ticketCall.data ?? this.ticket;
        this.lines = linesCall.data ?? [];
        this.linesTotal = linesCall.pagination?.total ?? this.lines.length;
        this.detailError = ticketCall.errorMessage ?? linesCall.errorMessage;
      },
    );
  }

  canDecideTicket(): boolean {
    return this.ticket?.apiStatus === 'new';
  }

  canDecideLine(line: SupplierApiTicketLine): boolean {
    return line.apiStatus === 'new';
  }

  canCompleteLine(line: SupplierApiTicketLine): boolean {
    return line.lineType === 'interim' && line.apiStatus === 'confirmed';
  }

  decideTicket(reject: boolean): void {
    const ticketId = this.ticket._id;
    this.actionService
      .run(
        {
          title: reject ? 'Refuser le ticket' : 'Confirmer le ticket',
          explanation: reject
            ? 'Toutes vos lignes de ce ticket passent au statut « Refusé ».'
            : 'Toutes vos lignes de ce ticket sont confirmées. Les lignes standards sont aussitôt finalisées, ' +
              'les lignes intérim / compétence attendent ensuite votre proposition de profil.',
          routeId: 'tickets-confirm',
          pathParams: { ticketId },
          body: { reject },
          danger: reject,
        },
        body => this.client.confirmTicket(ticketId, body),
      )
      .subscribe(() => this.loadDetail());
  }

  decideLine(line: SupplierApiTicketLine, reject: boolean): void {
    const ticketId = this.ticket._id;
    this.actionService
      .run(
        {
          title: reject ? 'Refuser la ligne' : 'Confirmer la ligne',
          explanation: reject
            ? 'Cette ligne passe au statut « Refusé ».'
            : line.lineType === 'interim'
              ? 'Cette ligne intérim / compétence passe au statut « Confirmé » et attend votre proposition de profil.'
              : 'Cette ligne est confirmée puis aussitôt finalisée. Le délai de disponibilité est enregistré ' +
                "seulement si la ligne n'en a pas déjà un.",
          routeId: 'tickets-line-confirm',
          pathParams: { ticketId, lineId: line.lineId },
          body: { reject },
          input: reject ? undefined : 'availabilityDelay',
          danger: reject,
        },
        body => this.client.confirmTicketLine(ticketId, line.lineId, body),
      )
      .subscribe(() => this.loadDetail());
  }

  completeLine(line: SupplierApiTicketLine, noProposalAvailable: boolean): void {
    const ticketId = this.ticket._id;
    this.actionService
      .run(
        {
          title: noProposalAvailable ? 'Signaler aucune proposition' : 'Proposer un profil',
          explanation: noProposalAvailable
            ? 'Vous indiquez ne pas pouvoir proposer de profil. La ligne est finalisée et le déclarant Predik décide de la suite.'
            : 'Le profil saisi est transmis au déclarant Predik, qui accepte la proposition ou abandonne la ligne.',
          routeId: 'tickets-line-complete',
          pathParams: { ticketId, lineId: line.lineId },
          body: { noProposalAvailable },
          input: noProposalAvailable ? undefined : 'selectedProfile',
          danger: noProposalAvailable,
        },
        body => this.client.completeTicketLine(ticketId, line.lineId, body),
      )
      .subscribe(() => this.loadDetail());
  }
}
